import { FiCpu } from 'react-icons/fi';

export const complexSentencesData = {
  courseId: 'complex-sentences',
  courseType: 'grammar',
  title: 'Complex Sentences',
  icon: FiCpu,
  overview: {
    impact: 'Essential for Band 7+ scores',
    focus: 'Combining clauses effectively',
    uses: 'Writing Task 1 & 2',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Building Complex Sentences',
      content: `<p>A complex sentence has one independent clause and at least one dependent clause. This structure shows grammatical sophistication.</p>`,
      examples: [
        { label: 'Simple', text: 'Technology has benefits. It also has drawbacks.' },
        { label: 'Complex', text: 'While technology has benefits, it also has drawbacks.' },
      ],
    },
    {
      title: 'Sentence Combination Strategies',
      content: `<p>Master these strategies to create varied, sophisticated sentences:</p>`,
      table: {
        headers: ['Strategy', 'Example'],
        rows: [
          ['Add relative clause', 'Technology, which has transformed society, continues to evolve.'],
          ['Add adverbial clause', 'Although challenges exist, technology remains beneficial.'],
          ['Use noun clause', 'Research shows that technology improves productivity.'],
          ['Combine with participle', 'Being expensive, this solution may not work for everyone.'],
        ],
      },
    },
    {
      title: 'Balancing Sentence Types',
      content: `<p>Don't overuse complex sentences. Mix them with simple and compound sentences for natural flow:</p>`,
      examples: [
        { 
          label: 'Good balance', 
          text: 'Technology has transformed education. However, while many benefits exist, challenges remain significant. These challenges include unequal access and the digital divide.' 
        },
      ],
      warning: 'Too many complex sentences can make your writing difficult to read. Balance is key!',
    },
  ],
  exercises: [
    {
      title: 'Combine the Sentences',
      type: 'writing',
      instructions: 'Combine these simple sentences into complex sentences.',
      questions: [
        {
          text: 'Technology is expensive. Many schools cannot afford it.',
          example: 'Because technology is expensive, many schools cannot afford it.',
          tip: 'Use "because" or "since" to show reason.',
        },
        {
          text: 'The government invests in education. Economic growth will follow.',
          example: 'If the government invests in education, economic growth will follow.',
          tip: 'Use "if" for conditional relationship.',
        },
        {
          text: 'Some people disagree. I believe this is the best approach.',
          example: 'Although some people disagree, I believe this is the best approach.',
          tip: 'Use "although" or "even though" for contrast.',
        },
      ],
    },
  ],
  tips: [
    'Use one complex sentence per paragraph as a minimum',
    'Vary your sentence openings',
    'Check that complex sentences are clear, not confusing',
  ],
  dailyPractice: [
    'Combine pairs of simple sentences',
    'Analyze IELTS model answers for sentence variety',
    'Practice different clause combinations',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Complex Sentences: Sentences with independent and dependent clauses, showing grammatical sophistication for higher IELTS bands.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...complexSentencesData,
      title: complexSentencesData.title + ' (Foundations)',
      overview: {
        ...complexSentencesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: complexSentencesData.sections.slice(0, Math.max(2, Math.ceil(complexSentencesData.sections.length / 2))),
      exercises: complexSentencesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...complexSentencesData,
      title: complexSentencesData.title + ' (Advanced)',
      overview: {
        ...complexSentencesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: complexSentencesData.sections,
      exercises: complexSentencesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(complexSentencesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return complexSentencesData;
}
